import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { booleanFilter } from '@valhalla/utils';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, shareReplay, take, exhaustMap, filter, tap } from 'rxjs/operators';

import { IApiResponse } from '../api-response';
import ApiVersion from '../api-versions';
import { DataHttpCommonService } from '../data-http-common.service';
import { ConfigurationDataHttpService, IAppConfiguration } from './abstract';
import { IApplicationSettingsDto, IAppSettingsAnonymousDto } from './dto';

@Injectable()
export class ConfigurationDataHttpServiceImpl implements ConfigurationDataHttpService {
	constructor(readonly http: HttpClient, readonly common: DataHttpCommonService) {}

	readonly refreshRequest$ = new BehaviorSubject(1);
	protected lastUpdateTimeAppSettingsJson: number;
	protected lastUpdateTimeConfiguration: number;
	readonly cacheTimeSec = 300;

	readonly appConfig$ = this.refreshRequest$.pipe(
		filter(() => this.needUpdate(this.lastUpdateTimeConfiguration)),
		map(_ => this.common.getApiUrl(`configuration`)),
		exhaustMap(url => this.http.get<IApiResponse<Record<string, any>>>(url)),
		map(({ data }) => data),
		tap(() => (this.lastUpdateTimeConfiguration = Date.now())),
		shareReplay({ bufferSize: 1, refCount: false })
	);

	readonly appSettingsAnonymousConfig$ = this.refreshRequest$.pipe(
		filter(() => this.needUpdate(this.lastUpdateTimeAppSettingsJson)),
		map(_ => this.common.getEndpointUrl(`/app-settings.json?t=${Date.now()}`)),
		exhaustMap(url => this.http.get<IAppSettingsAnonymousDto>(url)),
		tap(settings => {
			this.lastUpdateTimeAppSettingsJson = Date.now();
			const isSpaAdminMode = Boolean(localStorage.getItem('isSpaAdminMode'));
			if (isSpaAdminMode && typeof settings.CustomSettings === 'object') {
				settings.CustomSettings.isSpaAdminMode = true;
			}
		}),
		shareReplay({ bufferSize: 1, refCount: false })
	);

	readonly localizationsCache: Record<
		string,
		{
			state$: BehaviorSubject<Record<string, string>>;
			value$: Observable<Record<string, string>>;
			loaded: boolean;
			pending: boolean;
		}
	> = {};

	getAppConfiguration(): Observable<Partial<IAppConfiguration>> {
		this.refreshRequest$.next(1);
		return this.appConfig$.pipe(take(1));
	}

	getAppSettingsAnonymous(): Observable<IAppSettingsAnonymousDto> {
		this.refreshRequest$.next(1);
		return this.appSettingsAnonymousConfig$.pipe(take(1));
	}

	getLocalizations(culture: string): Observable<Record<string, string>> {
		if (!this.localizationsCache[culture]) {
			const state$ = new BehaviorSubject(null);
			this.localizationsCache[culture] = {
				loaded: false,
				pending: true,
				state$: state$,
				value$: state$.pipe(booleanFilter()),
			};
			this.getLocalizationsFromServer(culture).subscribe(data => {
				this.localizationsCache[culture].loaded = true;
				this.localizationsCache[culture].pending = false;
				state$.next(data);
			});
		}
		return this.localizationsCache[culture].value$;
	}

	protected needUpdate(lastUpdateTime: number) {
		return lastUpdateTime ? Date.now() - lastUpdateTime > this.cacheTimeSec * 1000 : true;
	}

	protected getLocalizationsFromServer(culture: string): Observable<Record<string, string>> {
		const url = this.common.getApiUrl(`configuration/language`, ApiVersion.v10);
		const queryParams = {
			culture,
		};
		return this.http.get<any>(url, { params: queryParams });
	}

	getAppConfigurationByName(): Observable<any> {
		const url = this.common.getApiUrl('configuration');
		return this.http.get(url);
	}

	getApplicationSettings(): Observable<IApplicationSettingsDto> {
		const url = this.common.getApiUrl('configuration/applicationSettings', ApiVersion.v10);
		return this.http.get<IApplicationSettingsDto>(url);
	}
}
